import React, {useState, useEffect} from 'react';
import {RefreshCw, Clock, Hash, Send} from 'lucide-react';
import Header from "../components/Header";
import api from "../utils/apiClient";
import {getConfigByStoreHash, getConfigHandle} from "../services/configService";
import {useLocation, useNavigate} from "react-router-dom";
import {useAppConfig} from "../context/AppConfigContext";
import {getLocationsUsingConfig} from "../services/locationService";
import {getStockDataUsingConfigId} from "../services/stockService";
import {reconcileOrder} from "../services/orderService";

const BigCommerceDashboard = ({appConfigId = null}) => {
    const { appConfig, setAppConfig } = useAppConfig();
    const [data, setData] = useState({
        count: appConfig?.stockData.count || 0,
        last_updated: appConfig?.stockData.last_updated,
        total_count: appConfig?.stockData.total_count || 0,
        pending_count: appConfig?.stockData.pending_count || 0,
        last_status: appConfig?.stockData.last_status
    });

    const {state, key, pathname}= useLocation();
    const [loading, setLoading] = useState(false);
    const [orderId, setOrderId] = useState('');
    const [reconciling, setReconciling] = useState(false);
    const [reconcileResult, setReconcileResult] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const signedPayload = queryParams.get("signed_payload");

        if (!appConfig || signedPayload !== appConfig?.signedPayload) {
            init(signedPayload, state);
        } else if (appConfig.stockData) {
            setData(appConfig.stockData);
        }
    }, [key, pathname, navigate, state]);


    const init = async (signedPayload,  state) => {
        let storeHash = state?.storeHash || null;
        if (signedPayload && !storeHash) {
            try {
                const res = await api.get(`/api/auth/load?signed_payload=${signedPayload}`, { noAuth: true });
                if (res?.data?.storeHash && res?.data?.token) {
                    localStorage.setItem("token", res.data.token);
                    storeHash = res.data.storeHash;
                }
            } catch (err) {
                console.error("Failed to fetch store hash and token:", err);
            }
        }

        if (!storeHash) return;

        try {
            let config = await getConfigByStoreHash(storeHash);

            if (!config?.fileUrl || !config?.fileType) {
                navigate("/config-add", {
                    state: {
                        storeHash,
                        ...(config?.fileUrl && { fileUrl: config.fileUrl }),
                        ...(config?.fileType && { fileType: config.fileType })
                    }
                });
                return;
            }

            const [stockRes, locations] = await Promise.all([
                getStockDataUsingConfigId(config.id).catch(err => {
                    console.error("Stock API failed:", err);
                    return { data: {} };
                }),
                getLocationsUsingConfig(config.id).catch(err => {
                    console.error("Locations API failed:", err);
                    return [];
                })
            ]);
            const contextData = {
                signedPayload, storeHash, savedConfig: config,
                savedLocations: locations,
                stockData: {
                    count: stockRes.count || 0,
                    last_updated: stockRes.last_updated,
                    total_count: stockRes.total_count,
                    pending_count: stockRes.pending_count || 0,
                    last_status: stockRes.last_status,
                },
            };
            console.log("the saved context is ", contextData);

            setAppConfig(contextData);
            setData(contextData.stockData);
        } catch (error) {
            console.error("Error in init: ", error);
        }
    };

    const refreshData = async () => {
        setLoading(true);
        const queryParams = new URLSearchParams(window.location.search);
        const signedPayloadParam = queryParams.get("signed_payload");
        await init(appConfig?.signedPayload || signedPayloadParam, state);
        setLoading(false);
    };

    const submitReconcile = async (e) => {
        e.preventDefault();
        const configId = appConfig?.savedConfig?.id;
        if (!orderId.trim() || !configId || reconciling) return;

        setReconciling(true);
        setReconcileResult(null);
        try {
            setReconcileResult(await reconcileOrder(orderId.trim(), configId));
        } catch (err) {
            const httpStatus = err?.response?.status;
            const serverSaid = err?.response?.data?.message;
            setReconcileResult({
                status: 'error',
                message: serverSaid
                    || (httpStatus ? `Request failed (${httpStatus}).` : 'Could not reach the server.')
            });
            console.error('Reconcile failed:', httpStatus, err?.response?.data || err?.message);
        } finally {
            setReconciling(false);
        }
    };

    const resultTone = (status) => {
        if (status === 'notified') return 'text-green-400';
        if (status === 'error' || status === 'not_found') return 'text-red-400';
        return 'text-gray-300';
    };

    const formatTime = (dateString) => {
        if (!dateString) return '--';
        const date = new Date(dateString);
        return isNaN(date.getTime())
            ? '--'
            : date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
    };

    return (
        <div className="min-h-screen bg-gray-900 p-6">
            {/* Header */}
            <Header />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                <div className="flex items-center justify-between mb-8 mt-8">
                    <h1 className="text-2xl font-bold text-white">Welcome</h1>
                    <button
                        onClick={refreshData}
                        disabled={loading}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg transition-colors"
                        style={{backgroundColor: "#1f2a38"}}
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}/>
                        <span>Refresh</span>
                    </button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
                    {/* Count Card */}
                    <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                        <div className="flex items-center space-x-3 mb-3">
                            <Hash className="w-5 h-5 text-blue-400"/>
                            <span className="text-gray-300 text-sm">Items Updated In Last Sync</span>
                        </div>
                        <p className="text-3xl font-bold text-white">{data.count}</p>
                        <p className="text-sm text-gray-400 mt-6">
                            Waiting To Sync: <span className="font-semibold text-white">{data.pending_count}</span>
                        </p>
                    </div>

                    {/* Last Updated Card */}
                    <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                        <div className="flex items-center space-x-3 mb-3">
                            <Clock className="w-5 h-5 text-green-400"/>
                            <span className="text-gray-300 text-sm">Last Sync & Total</span>
                        </div>
                        <p className="text-3xl font-bold text-white">{formatTime(data.last_updated)}</p>
                        {data.last_status && data.last_status !== 'Success' && (
                            <p className="text-sm text-red-400 mt-2">Last run {data.last_status}</p>
                        )}
                        {data.total_count !== undefined && (
                            <p className="text-sm text-gray-400 mt-6">
                                Total Count: <span className="font-semibold text-white">{data.total_count}</span>
                            </p>
                        )}
                    </div>
                </div>

                {/* Resend a single order to the dropshipper */}
                <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 max-w-2xl mt-6">
                    <div className="flex items-center space-x-3 mb-1">
                        <Send className="w-5 h-5 text-blue-400"/>
                        <span className="text-gray-300 text-sm">Check A Single Order</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-4">
                        Enter a BigCommerce order id to check it against retail stock and send it to the
                        dropshipper if anything is outstanding.
                    </p>

                    <form onSubmit={submitReconcile} className="flex items-center space-x-3">
                        <input
                            type="text"
                            value={orderId}
                            onChange={(e) => setOrderId(e.target.value)}
                            placeholder="Order ID"
                            className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white
                                       placeholder-gray-500 focus:outline-none focus:border-blue-500"
                        />
                        <button
                            type="submit"
                            disabled={reconciling || !orderId.trim()}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700
                                       disabled:opacity-50 text-white rounded-lg transition-colors"
                            style={{backgroundColor: "#1f2a38"}}
                        >
                            <RefreshCw className={`w-4 h-4 ${reconciling ? 'animate-spin' : ''}`}/>
                            <span>{reconciling ? 'Checking' : 'Check'}</span>
                        </button>
                    </form>

                    {reconcileResult && (
                        <div className="mt-4">
                            <p className={`text-sm ${resultTone(reconcileResult.status)}`}>
                                {reconcileResult.message}
                            </p>
                            {reconcileResult.items?.length > 0 && (
                                <ul className="mt-2 text-sm text-gray-400 space-y-1">
                                    {reconcileResult.items.map((item) => (
                                        <li key={item.sku}>
                                            {item.name} — {item.sku} × {item.quantity}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>

            </div>

        </div>
    );
};

export default BigCommerceDashboard;
